import { type ReactNode } from 'react';
import { CdnImage } from '../CdnImage';
import { VideoStill } from '../VideoStill';
import { CDN } from '../../lib/cdn';
import { SignUpButton, TalkToFoundersButton } from '../CTAButtons';
import { SegmentSeeker } from '../SegmentSeeker';
import { useInViewAutoAdvance } from '../../hooks/useInViewAutoAdvance';
import {
  AGENCIES_COMPARE_ROWS,
  AGENCIES_FAQ,
  ECON_AFTER,
  ECON_BEFORE,
  PV2_STAGES,
  SPEC_AGENCY,
  SPEC_WORKFLOW,
  type AgencyCompareCell,
} from '../../content/forAgenciesPage';
import { HeroLabel, PageFaqSection } from '../ds';
import { EditorialHighlights } from '../EditorialHighlights';

function AgencyVsCell({ cell }: { cell: AgencyCompareCell }) {
  return <span className={cell.tone}>{cell.label}</span>;
}

function Pv2Pipeline() {
  const { sectionRef, active, select, inView, interval, tick } =
    useInViewAutoAdvance(PV2_STAGES.length, { interval: 5000 });

  return (
    <section className="pv2 band-light" id="pipeline" ref={sectionRef}>
      <div className="container">
        <div className="sec-head reveal">
          <span className="label-mono">- The agency workflow</span>
          <h2>
            Brief in. <em>Finished cut</em> out.
          </h2>
          <p className="sub">
            Five automatic stages, brief to deliverable - with your creative director controlling every override.
          </p>
        </div>

        <div className="pv2-track reveal" role="tablist" aria-label="Pipeline stages">
          {PV2_STAGES.flatMap((s, i) => {
            const els: ReactNode[] = [];
            if (i > 0) {
              els.push(
                <span key={`arrow-${s.idx}`} className="pv2-arrow" aria-hidden="true">
                  →
                </span>,
              );
            }
            els.push(
              <button
                key={s.idx}
                type="button"
                className={`pv2-stage${active === s.idx ? ' active' : ''}`}
                data-idx={s.idx}
                role="tab"
                aria-selected={active === s.idx}
                aria-controls={`pv2-panel-${s.idx}`}
                onClick={() => select(s.idx)}
              >
                <div className="pv2-thumb">
                  <VideoStill src={s.video} aria-label={s.title} />
                  <span className="pv2-slabel">{s.label}</span>
                </div>
              </button>,
            );
            return els;
          })}
        </div>

        <div className="pv2-panels">
          {PV2_STAGES.map((s) => (
            <div
              key={s.idx}
              className={`pv2-panel${active === s.idx ? ' active' : ''}`}
              id={`pv2-panel-${s.idx}`}
              data-idx={s.idx}
              role="tabpanel"
              hidden={active !== s.idx}
            >
              <div className="pv2-panel-still">
                {active === s.idx && (
                  <VideoStill src={s.video} aria-label={s.title} />
                )}
                <div className="pv2-panel-overlay" />
              </div>
              <div className="pv2-panel-info">
                <span className="pv2-pnum">{s.pnum}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                <div className="pv2-panel-tags">
                  {s.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <SegmentSeeker active={active} interval={interval} tick={tick} inView={inView} />
      </div>
    </section>
  );
}

export function ForAgenciesSections() {
  return (
    <div className="solutions-page">
      <header className="fm-hero band-dark">
        <div className="container hero-grid">
            <div className="reveal">
              <HeroLabel>For agencies &amp; creative production teams</HeroLabel>
              <h1 className="hero-headline">
                Branded Films, From Script to Cut, on a Deadline
              </h1>
              <p className="hero-sub">
                Brief to cinematic delivery in one place. Brand continuity across every project, four engines per
                shot, full commercial rights on every tier.
              </p>
              <div className="hero-ctas">
                <SignUpButton variant="primary" />
                <a href="#pipeline" className="btn btn-ghost">
                  See the pipeline
                </a>
              </div>
              <div className="hero-note">
                Commercial rights on every tier · Per-client project isolation
              </div>
            </div>
            <div
              className="hero-still reveal d2"
              role="img"
              aria-label="AI video production for agencies - a branded script becoming an on-brand film in Induce"
            >
              <VideoStill
                src={CDN.videos.agencyHeroPortrait}
                aria-label="AI video production for agencies - a branded script becoming an on-brand film in Induce"
              />
            </div>
        </div>
      </header>

      <section className="answer band-light" aria-labelledby="answer-h">
        <div className="container">
          <div className="tag">- What is Induce</div>
          <div className="answer-body">
            <h2 id="answer-h" className="a-lead">
              <b>Infrastructure, not another subscription.</b>
            </h2>
            <p className="a-body">
              Induce is AI video infrastructure, not a prompt box. Give it a client brief or screenplay and it returns
              a continuous cinematic cut, with brand continuity held across every scene and project.
            </p>
            <p className="a-body">
              Video at volume means five subscriptions, manual handoffs, and brand drift. Induce runs breakdown,
              generation, continuity, and export in one place — one billing line, brand right in shot 1 and shot 40.
            </p>
            <div className="a-stats reveal">
              <div className="a-stat">
                <b>1</b>
                <span>Pipeline. Not a stack of tools.</span>
              </div>
              <div className="a-stat">
                <b>4</b>
                <span>Engines per project - Kling, Seedance, Veo, Nano Banana</span>
              </div>
              <div className="a-stat">
                <b>∞</b>
                <span>Brand continuity across scenes</span>
              </div>
              <div className="a-stat">
                <b>Every tier</b>
                <span>Full commercial rights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="econ band-dark" id="economics" aria-label="Production cost comparison">
        <div className="container">
          <div className="sec-head reveal" style={{ gridColumn: '1 / -1' }}>
            <span className="label-mono">- The production math</span>
            <h2>
              Weeks and Five Figures. Or <em>Hours and Three.</em>
            </h2>
          </div>
          <div className="econ-col before reveal">
            <div className="etag">{ECON_BEFORE.etag}</div>
            <h3>
              {ECON_BEFORE.title} <em>{ECON_BEFORE.titleEm}</em>
            </h3>
            <div className="econ-rows">
              {ECON_BEFORE.rows.map((row) => (
                <div key={row.label} className="econ-row">
                  <span className="item">{row.label}</span>
                  <span className="val">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="econ-div" aria-hidden="true" />
          <div className="econ-col after reveal">
            <div className="etag">{ECON_AFTER.etag}</div>
            <h3>{ECON_AFTER.title}</h3>
            <div className="econ-rows">
              {ECON_AFTER.rows.map((row) => (
                <div key={row.label} className="econ-row">
                  <span className="item">{row.label}</span>
                  <span className="val">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Pv2Pipeline />

      <EditorialHighlights
        label="Agency workflows"
        items={[
          { label: 'Faster deliverables', title: 'AI B-Roll Generator for Faster Turnaround', body: 'Generate on-brand b-roll in hours, not days — matched to your brief\'s continuity model, ready to cut into any deliverable.' },
          { label: 'Production value', title: 'Why Agencies Choose Induce Over a Production Company', body: 'Same cinematic standard as a production company, at a fraction of the cost per finished minute — with full commercial rights on every tier.' },
        ]}
      />

      <section className="bento-section band-dark" id="deliverables">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- What your clients get</span>
            <h2>
              Every format your clients brief. <em>One pipeline.</em>
            </h2>
            <p className="sub">
              Built for narrative, brand-consistent video - campaign creative, product storytelling, brand spots, and
              social series at scale.
            </p>
          </div>

          <div className="bento-grid reveal">
            <div className="bc bc-top-left">
              <div className="bc-ui">
                <div className="bc-ui-bar">
                  <span className="bc-ui-dot" />
                  <span className="bc-ui-dot" />
                  <span className="bc-ui-dot" />
                </div>
                <div className="bc-ui-row">
                  <span className="bc-ui-label">CLIENT</span>
                  <span className="bc-ui-val">Brand Co.</span>
                </div>
                <div className="bc-ui-row">
                  <span className="bc-ui-label">BRIEF</span>
                  <span className="bc-ui-val">Q3 campaign - hero reel</span>
                </div>
                <div className="bc-ui-row">
                  <span className="bc-ui-label">SCENES</span>
                  <span className="bc-ui-val">12 · auto-broken down</span>
                </div>
                <div className="bc-ui-row">
                  <span className="bc-ui-label">STATUS</span>
                  <span className="bc-ui-val hot">Generating</span>
                </div>
              </div>
              <div className="bc-meta">
                <div className="bc-kicker">Script to delivery</div>
                <h3>Brief in. Finished cut out.</h3>
                <p>One upload. Full breakdown. Cinematic cut delivered.</p>
              </div>
            </div>

            <div className="bc bc-bottom-left">
              <div className="bc-still">
                <CdnImage
                  src={CDN.agencies.crossClientIsolation}
                  alt="Cross-client isolation - each client in their own world"
                  loading="lazy"
                />
              </div>
              <div className="bc-overlay" />
              <div className="bc-meta anchored">
                <div className="bc-kicker">Cross-client isolation</div>
                <h3>Each client. Their own world.</h3>
              </div>
            </div>

            <div className="bc bc-hero">
              <div className="bc-still">
                <CdnImage
                  src={CDN.agencies.agencyFeatures}
                  alt="Agency features - what your clients see"
                  loading="lazy"
                />
              </div>
              <div className="bc-overlay hero-overlay" />
              <div className="bc-hero-body">
                <div className="bc-kicker">Agency features</div>
                <h2 className="bc-hero-title">
                  What your
                  <br />
                  clients see.
                </h2>
                <SignUpButton variant="primary" className="bc-hero-cta" />
              </div>
            </div>

            <div className="bc bc-top-right">
              <div className="bc-still">
                <CdnImage
                  src={CDN.agencies.multiFormatDelivery}
                  alt="Multi-format delivery - broadcast, social, and pre-roll from one brief"
                  loading="lazy"
                />
              </div>
              <div className="bc-overlay" />
              <div className="bc-angle-ui">
                <div className="bc-angle-row active">Broadcast :30</div>
                <div className="bc-angle-row">Social 9:16</div>
                <div className="bc-angle-row">Pre-roll :15</div>
              </div>
              <div className="bc-meta anchored">
                <div className="bc-kicker">Multi-format delivery</div>
                <h3>One brief. Every format.</h3>
              </div>
            </div>

            <div className="bc bc-mid-right">
              <div className="bc-tag-ui">
                <div className="bc-tag-line">
                  <span className="bc-tag client">Brand Co.</span>
                  <span className="bc-tag-text">hero reel for</span>
                  <span className="bc-tag location">Q3 Summer</span>
                </div>
                <div className="bc-tag-line">
                  <span className="bc-tag-text">featuring</span>
                  <span className="bc-tag character">@Protagonist</span>
                  <span className="bc-tag-text">at</span>
                  <span className="bc-tag location">@CityRooftop</span>
                </div>
              </div>
              <div className="bc-meta">
                <div className="bc-kicker">Brand continuity</div>
                <h3>Characters held across every scene.</h3>
              </div>
            </div>

            <div className="bc bc-bottom-right">
              <div className="bc-still">
                <CdnImage
                  src={CDN.agencies.commercialRights}
                  alt="Commercial rights - every asset yours to deliver"
                  loading="lazy"
                />
              </div>
              <div className="bc-overlay" />
              <div className="bc-meta anchored">
                <div className="bc-kicker">Commercial rights</div>
                <h3>Every asset. Every client. Yours to deliver.</h3>
                <p>Full commercial licensing - every tier, every project.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="band-light" id="compare">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- How Induce compares</span>
            <h2>
              One pipeline vs. <em>four subscriptions.</em>
            </h2>
            <p className="sub">
              Most AI video is a prompt box or an avatar tool. Here&apos;s where a brief-to-delivery pipeline pulls ahead
              at volume.
            </p>
          </div>
          <div className="vs-dt-wrap reveal">
            <table className="vs-dt">
              <caption className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Comparison of Induce versus Runway Gen-4, HeyGen, and a multi-tool stack for agencies
              </caption>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '19%' }} />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th className="th-cap" scope="col">
                    Capability
                  </th>
                  <th className="th-induce ci" scope="col">
                    <span className="tool-name">Induce</span>
                    <span className="tool-sub">Pipeline-first</span>
                  </th>
                  <th className="th-comp" scope="col">
                    <span className="tool-name">Runway Gen-4</span>
                    <span className="tool-sub">Clip / VFX suite</span>
                  </th>
                  <th className="th-comp" scope="col">
                    <span className="tool-name">HeyGen</span>
                    <span className="tool-sub">Avatar video</span>
                  </th>
                  <th className="th-comp" scope="col">
                    <span className="tool-name">Multi-tool stack</span>
                    <span className="tool-sub">Runway + HeyGen + Kling</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {AGENCIES_COMPARE_ROWS.map((row) => (
                  <tr key={row.capability}>
                    <th scope="row">{row.capability}</th>
                    <td className="ci">
                      <AgencyVsCell cell={row.induce} />
                    </td>
                    <td>
                      <AgencyVsCell cell={row.runway} />
                    </td>
                    <td>
                      <AgencyVsCell cell={row.heygen} />
                    </td>
                    <td>
                      <AgencyVsCell cell={row.stack} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="vs-note">
            Comparison reflects publicly available product information as of May 2026. Competitor names are
            trademarks of their respective owners.
          </p>
        </div>
      </section>

      <section className="spec band-dark" id="platform">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Platform spec</span>
            <h2>
              Built for the <em>production team.</em>
            </h2>
          </div>
          <div className="spec-grid reveal">
            <div className="spec-box">
              <div className="shead">
                <span className="lbl">{SPEC_WORKFLOW.title}</span>
                <span className="sbadge">{SPEC_WORKFLOW.badge}</span>
              </div>
              {SPEC_WORKFLOW.rows.map((row) => (
                <div key={row.key} className="spec-row">
                  <span className="skey">{row.key}</span>
                  <span className={`sval${'highlight' in row && row.highlight ? ` ${row.highlight}` : ''}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
            <div className="spec-box">
              <div className="shead">
                <span className="lbl">{SPEC_AGENCY.title}</span>
                <span className="sbadge">{SPEC_AGENCY.badge}</span>
              </div>
              {SPEC_AGENCY.rows.map((row) => (
                <div key={row.key} className="spec-row">
                  <span className="skey">{row.key}</span>
                  <span
                    className={`sval${'yes' in row && row.yes ? ' yes' : ''}${'highlight' in row && row.highlight ? ` ${row.highlight}` : ''}`}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PageFaqSection
        eyebrow="- Asked &amp; answered"
        title={
          <>
            Straight answers for
            <br />
            <em>agencies.</em>
          </>
        }
        items={AGENCIES_FAQ}
      />

      <section className="wo-cta band-dark" id="cta">
        <div className="container">
          <div className="wo reveal">
          <div className="wo-top">
            <div className="wo-field">
              <span className="wo-label">CLIENT</span>
              <span className="wo-val">Your Brand</span>
            </div>
            <div className="wo-field">
              <span className="wo-label">AGENCY</span>
              <span className="wo-val">Your Team</span>
            </div>
            <div className="wo-field">
              <span className="wo-label">OUTPUT</span>
              <span className="wo-val">Campaign cut</span>
            </div>
            <div className="wo-field">
              <span className="wo-label">STATUS</span>
              <span className="wo-val hot">Ready to brief</span>
            </div>
          </div>
          <div className="wo-body">
            <div className="wo-status">Pipeline active</div>
            <h2>
              Brief in, branded film out
              <br />
              <em>on the clock.</em>
            </h2>
            <div className="wo-acts">
              <TalkToFoundersButton variant="primary" />
              <SignUpButton variant="ghost" />
            </div>
            <p className="wo-note">Commercial rights on every tier · Per-client project isolation</p>
          </div>
          </div>
        </div>
      </section>
    </div>
  );
}
